import { Box, Container, Grid, Typography, useTheme } from "@mui/material";
import { useMediaQuery } from "@mui/material";
import { useTranslation } from "react-i18next";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import SmartphoneIcon from "@mui/icons-material/Smartphone";
import CloudSyncIcon from "@mui/icons-material/CloudSync";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

export const HomeOpenSource = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const qaItems = [
    {
      question: t("Isn't this complicated?"),
      answer: t(
        "It can be, but we are here to guide you step-by-step and make it easier."
      ),
    },
    {
      question: t("Will this really work for me?"),
      answer: t(
        "Tens of thousands of people around the world have seen life-changing results."
      ),
    },
    {
      question: t("Is it safe?"),
      answer: t(
        "All automated insulin delivery systems—both commercial and open-source—carry certain risks. Research has shown that open-source looping systems are <a href='https://www.liebertpub.com/doi/full/10.1089/dia.2020.0535#:~:text=The%20Loop%20open%20source%20system%20can%20be%20initiated%20with%20community%2Ddeveloped%20resources%20and%20used%20safely%20and%20effectively%20by%20adults%20and%20children%20with%20type%201%20diabetes.' target='_blank'>just as safe as commercial systems</a>."
      ),
    },
  ];

  const metrics = [
    {
      icon: (
        <Box
          component="img"
          src="/assets/integrated/androidaps.png"
          alt="AndroidAPS logo - Open source Android app for automated insulin delivery"
          loading="lazy"
          width="40"
          height="40"
          sx={{ width: 40, height: 40, borderRadius: 2, background: "white" }}
        />
      ),
      number: "13,900+",
      description: t("Users of AndroidAPS"),
    },
    {
      icon: (
        <Box
          component="img"
          src="/assets/integrated/loop.png"
          alt="Loop logo - Open source iOS app for automated insulin delivery"
          loading="lazy"
          width="40"
          height="40"
          sx={{ width: 40, height: 40, borderRadius: 2, background: "white" }}
        />
      ),
      number: "33,400+",
      description: t("Users of Loop"),
    },
    {
      icon: (
        <Box
          component="img"
          src="/assets/integrated/nightscout.png"
          alt="Nightscout logo - Open source cloud-based CGM data visualization platform"
          loading="lazy"
          width="40"
          height="40"
          sx={{
            width: 40,
            height: 40,
            filter: theme.palette.mode === "dark" ? "invert(1)" : "none",
          }}
        />
      ),
      number: "72,500+",
      description: t("Users of Nightscout"),
    },
    {
      icon: (
        <AccessTimeIcon
          sx={{ fontSize: 40, color: theme.palette.primary.main }}
        />
      ),
      number: t("Millions"),
      description: t("Of real-world testing hours accumulated"),
    },
  ];

  return (
    <Box
      sx={{
        py: 8,
        backgroundColor: theme.palette.background.default,
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={6} alignItems="center">
          {/* Left Column - Text Content */}
          <Grid item xs={12} md={6}>
            <Typography
              component="h2"
              variant="h3"
              sx={{
                fontWeight: 700,
                color: theme.palette.text.primary,
                mb: 3,
                fontSize: { xs: "2rem", md: "2.5rem" },
              }}
            >
              {t("Is open-source for me?")}
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: theme.palette.text.primary,
                fontSize: "1.1rem",
                lineHeight: 1.7,
                mb: 4,
              }}
            >
              {t(
                "Maybe you're interested in open-source looping, but still in doubt whether you should start. We get it, we have been there too. Our goal is to make sure you feel safe and thoroughly understand the risks and opportunities in using open-source software so you can make a thoughtful decision."
              )}
            </Typography>

            <Box sx={{ space: 3 }}>
              {qaItems.map((item, index) => (
                <Box key={index} sx={{ mb: 3 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.text.primary,
                      mb: 1,
                    }}
                  >
                    {item.question}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: theme.palette.text.primary,
                      fontSize: "1rem",
                      lineHeight: 1.6,
                    }}
                    dangerouslySetInnerHTML={{
                      __html: item.answer.replace(
                        /<a\s+(?:[^>]*?\s+)?href=["']([^"']*)["']([^>]*)>(.*?)<\/a>/g,
                        (match: string, url: string, attrs: string, text: string) =>
                          `<a href="${url}" style="color: ${theme.palette.primary.main}; text-decoration: underline;">${text}</a>`
                      ),
                    }}
                  />
                </Box>
              ))}
            </Box>
          </Grid>

          {/* Right Column - Metrics */}
          <Grid item xs={12} md={6}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {metrics.map((metric, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 3,
                    p: 3,
                    borderRadius: 2,
                    backgroundColor: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.divider}`,
                    transition:
                      "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: theme.shadows[4],
                    },
                  }}
                >
                  <Box sx={{ flexShrink: 0 }}>{metric.icon}</Box>
                  <Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 700,
                        color: theme.palette.text.primary,
                        fontSize: { xs: "2rem", md: "2.5rem" },
                        lineHeight: 1,
                      }}
                    >
                      {metric.number}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: theme.palette.text.primary,
                        fontSize: "1rem",
                        lineHeight: 1.5,
                        mt: 1,
                      }}
                    >
                      {metric.description}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};
